import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Loader2, Send, FileSpreadsheet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const HEADERS = ['Dipendente', 'GG previsti', 'GG lavorati', 'Ore', 'GG ritardo', 'Min ritardo', 'Min anticipi',
  'Ferie gg', 'Permessi h', 'Malattia gg', 'Ingiustificate', 'Saldo ferie', 'Saldo perm.'];
const rowVals = (r) => [r.name, r.scheduled_days, r.days_worked, r.worked_hours, r.late_days, r.late_minutes,
  r.early_exit_minutes, r.ferie_days, r.permesso_hours, r.malattia_days, r.unjustified_days, r.ferie_balance, r.rol_balance];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

export const AdminReportTab = () => {
  const prev = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
  const [year, setYear] = useState(prev.getFullYear());
  const [month, setMonth] = useState(prev.getMonth() + 1);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [recipient, setRecipient] = useState('segreteria@cedelabora.it');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        axios.get(`${API}/hr/report/preview?year=${year}&month=${month}`, { headers: authHeaders() }),
        axios.get(`${API}/hr/report/history`, { headers: authHeaders() }),
      ]);
      setPreview(p.data);
      setHistory(h.data.reports || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore caricamento report'); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!window.confirm(`Inviare il report ${String(month).padStart(2, '0')}/${year} (Excel + PDF) a ${recipient}?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/hr/report/send`, { year, month, to_email: recipient }, { headers: authHeaders() });
      toast.success(`Report inviato a ${r.data.recipient} ✅`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invio fallito'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="hr-report-tab">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-600 flex items-center gap-2">
        <FileSpreadsheet className="h-4 w-4 text-emerald-600 shrink-0" />
        Il report del mese precedente viene inviato <b>automaticamente il 3 di ogni mese</b> a segreteria@cedelabora.it (Excel + PDF). Da qui puoi vederlo in anteprima e inviarlo manualmente.
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div><Label className="text-xs">Mese</Label>
          <select data-testid="hr-rep-month" className="border rounded-md h-9 px-2 text-sm block" value={month} onChange={e => setMonth(Number(e.target.value))}>
            {MONTHS.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Anno</Label>
          <Input type="number" data-testid="hr-rep-year" className="w-24" value={year} onChange={e => setYear(Number(e.target.value))} /></div>
        <div className="flex-1 min-w-48"><Label className="text-xs">Destinatario</Label>
          <Input data-testid="hr-rep-recipient" value={recipient} onChange={e => setRecipient(e.target.value)} /></div>
        <Button data-testid="hr-rep-send-btn" onClick={send} disabled={sending || !preview?.rows?.length}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Invia ora
        </Button>
      </div>

      {!loading && preview?.rows?.length > 0 && preview.rows.every(r => r.days_worked === 0) && preview.rows.some(r => r.scheduled_days > 0) && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm text-amber-800" data-testid="hr-rep-fill-hint">
          ⚠️ <b>Nessuna presenza registrata in questo mese.</b> Se le timbrature non erano attive, vai nel tab
          <b> "Presenze"</b>, seleziona il mese e premi <b>"Compila mese"</b>: i giorni previsti dall'orario verranno
          marcati come lavorati (ferie/malattie escluse automaticamente) e questo report si aggiornerà.
        </div>
      )}

      {loading ? <p className="text-sm text-slate-400 py-4 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></p> : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl">
          <table className="w-full text-xs" data-testid="hr-rep-preview-table">
            <thead><tr className="bg-slate-900 text-white">
              {HEADERS.map(h => <th key={h} className="py-2 px-2 text-left font-semibold whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {(!preview?.rows || preview.rows.length === 0) && <tr><td colSpan={HEADERS.length} className="py-6 text-center text-slate-400" data-testid="hr-rep-empty">Nessun dato nel mese selezionato</td></tr>}
              {(preview?.rows || []).map((r, i) => (
                <tr key={i} className={i % 2 ? 'bg-slate-50' : ''}>
                  {rowVals(r).map((v, j) => <td key={j} className={`py-1.5 px-2 ${j === 0 ? 'font-medium' : ''} ${j === 10 && v > 0 ? 'text-rose-600 font-bold' : ''}`}>{v}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div data-testid="hr-rep-history">
        <p className="text-sm font-semibold text-slate-700 mb-2">Storico invii</p>
        {history.length === 0 ? <p className="text-sm text-slate-400">Nessun invio effettuato</p> : (
          <div className="space-y-1">
            {history.map(h => (
              <p key={h.id} className="text-sm text-slate-600">
                {h.sent ? '✅' : '❌'} {String(h.month).padStart(2, '0')}/{h.year} → {h.recipient}
                <span className="text-slate-400"> · {h.trigger} · {new Date(h.sent_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · {h.employees} dipendenti</span>
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
